import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { playNote, play808 } from "@/lib/audio";
import api from "@/lib/api";
import Visualizer from "@/components/Visualizer";
import Keyboard from "@/components/Keyboard";
import {
  Lightning, X, Minus, Square as SquareIcon, Sparkle, WaveSawtooth, WaveSine,
  WaveSquare, WaveTriangle, Play, FloppyDisk, Cpu, Gear, Download as DownloadIcon
} from "@phosphor-icons/react";

const DEFAULT = {
  name: "VOID_FORGE_808",
  waveform: "sawtooth",
  sub_octave: -1, drive: 0.65, saturation: 0.5, bitcrush: 0.15,
  filter_cutoff: 0.55, filter_resonance: 0.45, attack: 0.02, decay: 0.2,
  sustain: 0.65, release: 0.3, reverb: 0.4, delay: 0.25, chorus: 0.2,
  stereo_width: 0.8, pitch_semitones: 0, detune_cents: 8, lfo_rate: 2.4,
  lfo_depth: 0.18, glide_ms: 60, tags: ["trap","808","void"],
  color_primary: "#FF003C", color_secondary: "#8B00FF",
  key: "C minor", root_note: "C", mode: "minor", bpm_suggestion: 140,
  chord_progression: ["Cm", "Ab", "Eb", "Bb"], scale: "natural minor",
};

const WAVE = [
  { v: "sine", I: WaveSine, l: "SIN" },
  { v: "sawtooth", I: WaveSawtooth, l: "SAW" },
  { v: "square", I: WaveSquare, l: "SQR" },
  { v: "triangle", I: WaveTriangle, l: "TRI" },
];

export default function PluginShowcase() {
  const [preset, setPreset] = useState(DEFAULT);
  const [bank, setBank] = useState("FACTORY");
  const [prompt, setPrompt] = useState("");

  const u = (k, v) => setPreset((p) => ({ ...p, [k]: v }));

  const audition = () => {
    [60, 63, 67, 70].forEach((n, i) => setTimeout(() => playNote(preset, n, 0.4), i * 180));
  };

  const generate = async () => {
    if (!prompt) return;
    try {
      const { data } = await api.post("/ai/generate-preset-public", { prompt });
      setPreset(data.preset);
      setTimeout(() => playNote(data.preset, 60, 0.7), 100);
    } catch {}
  };

  return (
    <div className="relative" data-testid="plugin-showcase-page">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute inset-0 scanlines opacity-30 pointer-events-none" />

      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 py-14">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-3">◢ THE PLUGIN</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            THIS IS <span className="text-brand-red neon-red">BEAT-LAB.</span>
          </h1>
          <p className="text-ink-mute text-sm mt-5 max-w-xl mx-auto">
            Interactive replica of the VST UI you&apos;ll see inside FL Studio, Ableton, Logic Pro, and any other DAW.
            Real audio. Real synthesis. Powered by Web Audio.
          </p>
        </div>

        {/* Plugin chrome frame */}
        <div className="mx-auto max-w-[1280px]">
          {/* Plugin window chrome (title bar) */}
          <div className="bg-gradient-to-b from-zinc-800 via-zinc-900 to-black border-x border-t border-zinc-700 px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lightning size={14} weight="fill" className="text-brand-red" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-ink-mute">
                BEAT-LAB <span className="text-brand-red">/</span> THE 808 — v1.0.4
              </span>
              <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim border-l border-zinc-700 pl-3 ml-1">
                FL STUDIO PATCHER · CH 1
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button className="w-6 h-6 hover:bg-zinc-700 flex items-center justify-center text-ink-mute">
                <Minus size={12} weight="bold" />
              </button>
              <button className="w-6 h-6 hover:bg-zinc-700 flex items-center justify-center text-ink-mute">
                <SquareIcon size={10} weight="bold" />
              </button>
              <button className="w-6 h-6 hover:bg-brand-red hover:text-black flex items-center justify-center text-ink-mute">
                <X size={12} weight="bold" />
              </button>
            </div>
          </div>

          {/* Plugin menu bar */}
          <div className="bg-bg-surface2 border-x border-zinc-700 px-4 py-2 flex items-center gap-5 text-[10px] tracking-[0.2em] uppercase">
            {["FILE", "EDIT", "VIEW", "PRESETS", "HELP"].map((m) => (
              <span key={m} className="text-ink-mute hover:text-white cursor-pointer">{m}</span>
            ))}
            <div className="ml-auto flex items-center gap-3 text-[9px] tracking-[0.3em] uppercase text-ink-dim">
              <span>CPU 12%</span>
              <span>·</span>
              <span>44.1k / 64</span>
              <Gear size={11} weight="bold" className="hover:text-white cursor-pointer" />
            </div>
          </div>

          {/* Plugin body */}
          <div className="bg-bg border-x border-b border-zinc-700 p-5 lg:p-6 grain relative">
            {/* Top: preset name + bank selector */}
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <div className="flex items-center gap-2 border border-zinc-800 bg-black/60 px-3 py-2">
                <Sparkle size={12} weight="fill" className="text-brand-red" />
                <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">PRESET</span>
                <span className="font-display font-bold tracking-tight text-base ml-1">{preset.name}</span>
              </div>
              <div className="flex border border-zinc-800">
                {["FACTORY", "USER", "AI"].map((b) => (
                  <button
                    key={b}
                    onClick={() => setBank(b)}
                    data-testid={`plugin-bank-${b}`}
                    className={`text-[10px] tracking-[0.3em] uppercase px-3 py-2 transition-colors ${
                      bank === b ? "bg-brand-red text-black" : "text-ink-mute hover:text-white"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
              <div className="flex border border-zinc-800 focus-within:border-brand-red flex-1 min-w-[200px]">
                <span className="px-2 text-brand-red font-bold flex items-center">›</span>
                <input
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && generate()}
                  data-testid="plugin-prompt-input"
                  placeholder="AI prompt — type a vibe…"
                  className="flex-1 bg-transparent py-2 text-sm font-mono outline-none"
                />
                <button onClick={generate} className="bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-3 hover:bg-white" data-testid="plugin-prompt-gen">
                  GEN
                </button>
              </div>
              <button onClick={audition} className="border border-zinc-700 text-[10px] tracking-[0.3em] uppercase px-3 py-2 hover:border-brand-purple hover:text-brand-purple inline-flex items-center gap-1.5" data-testid="plugin-audition">
                <Play size={11} weight="fill" /> AUDITION
              </button>
            </div>

            {/* AUTO KEY DETECTION strip */}
            {preset.key && (
              <div className="mb-5 flex items-center gap-3 flex-wrap text-[10px] tracking-[0.25em] uppercase border border-brand-purple/40 bg-brand-purple/5 px-3 py-2" data-testid="plugin-key-detect">
                <span className="text-brand-purple flex items-center gap-1.5 font-bold">
                  <span className="w-1.5 h-1.5 bg-brand-purple rounded-full animate-pulse" /> AUTO KEY DETECT
                </span>
                <span className="text-white font-display text-base tracking-tight" data-testid="plugin-detected-key">{preset.key.toUpperCase()}</span>
                <span className="text-ink-dim">·</span>
                <span className="text-brand-red font-display text-base">{preset.bpm_suggestion} BPM</span>
                <span className="text-ink-dim">·</span>
                <span className="text-ink-mute font-mono text-xs">{preset.chord_progression?.join(" → ")}</span>
                <span className="ml-auto text-ink-dim text-[9px]">SCALE: {preset.scale}</span>
              </div>
            )}

            {/* Main grid: OSC + FX + 808 + KB */}
            <div className="grid grid-cols-12 gap-3">
              {/* OSCILLATOR */}
              <Module title="OSCILLATOR" testid="plugin-osc" className="col-span-12 lg:col-span-5 row-span-2">
                <Visualizer height={100} style="wave" color={preset.color_primary} color2={preset.color_secondary} />
                <div className="grid grid-cols-4 gap-1.5 mt-3">
                  {WAVE.map(({ v, I, l }) => (
                    <button
                      key={v}
                      onClick={() => u("waveform", v)}
                      data-testid={`plugin-wave-${v}`}
                      className={`border p-2 flex flex-col items-center gap-0.5 transition-colors ${
                        preset.waveform === v ? "border-brand-red text-white bg-brand-red/10" : "border-zinc-800 text-ink-mute hover:border-zinc-600"
                      }`}
                    >
                      <I size={16} weight="bold" />
                      <span className="text-[9px] tracking-[0.2em] uppercase">{l}</span>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <RotaryKnob label="DRIVE"   value={preset.drive}      onChange={(v) => u("drive", v)} testid="plugin-knob-drive" />
                  <RotaryKnob label="SAT"     value={preset.saturation} onChange={(v) => u("saturation", v)} testid="plugin-knob-saturation" />
                  <RotaryKnob label="CRUSH"   value={preset.bitcrush}   onChange={(v) => u("bitcrush", v)} testid="plugin-knob-bitcrush" />
                </div>
              </Module>

              {/* FILTER + ENVELOPE */}
              <Module title="FILTER / ENVELOPE" testid="plugin-filter" className="col-span-12 lg:col-span-4 row-span-2">
                <div className="grid grid-cols-2 gap-3">
                  <RotaryKnob label="CUTOFF"   value={preset.filter_cutoff}    onChange={(v) => u("filter_cutoff", v)} testid="plugin-knob-cutoff" />
                  <RotaryKnob label="RES"      value={preset.filter_resonance} onChange={(v) => u("filter_resonance", v)} testid="plugin-knob-res" />
                  <RotaryKnob label="ATTACK"   value={preset.attack}           onChange={(v) => u("attack", v)} testid="plugin-knob-attack" />
                  <RotaryKnob label="DECAY"    value={preset.decay}            onChange={(v) => u("decay", v)} testid="plugin-knob-decay" />
                  <RotaryKnob label="SUSTAIN"  value={preset.sustain}          onChange={(v) => u("sustain", v)} testid="plugin-knob-sustain" />
                  <RotaryKnob label="RELEASE"  value={preset.release}          onChange={(v) => u("release", v)} testid="plugin-knob-release" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <RotaryKnob label="LFO RATE" value={preset.lfo_rate}  min={0.1} max={10} step={0.1} unit="Hz" onChange={(v) => u("lfo_rate", v)} testid="plugin-knob-lfo-rate" />
                  <RotaryKnob label="LFO DEPTH" value={preset.lfo_depth} onChange={(v) => u("lfo_depth", v)} testid="plugin-knob-lfo-depth" />
                </div>
              </Module>

              {/* 808 DESIGNER */}
              <Module title="808 DESIGNER" tag="MOD A" testid="plugin-808" className="col-span-12 lg:col-span-3 row-span-2">
                <Visualizer height={70} style="bars" />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <RotaryKnob label="PUNCH" value={0.65} onChange={() => {}} testid="plugin-knob-punch" small />
                  <RotaryKnob label="SUB"   value={0.85} onChange={() => {}} testid="plugin-knob-sub" small />
                  <RotaryKnob label="GLIDE" value={0.18} onChange={() => {}} testid="plugin-knob-glide" small />
                  <RotaryKnob label="TUNE"  value={0.5}  onChange={() => {}} testid="plugin-knob-tune" small />
                </div>
                <button
                  onClick={() => play808({ pitch: 36, drive: preset.drive, glide: 0.15, punch: 0.7, sub: 0.85, saturation: preset.saturation, duration: 0.7 })}
                  data-testid="plugin-808-trigger"
                  className="mt-3 w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-2 hover:bg-white"
                >
                  ▸ 808
                </button>
                <div className="grid grid-cols-4 gap-1 mt-2">
                  {[30, 36, 40, 43].map((p) => (
                    <button
                      key={p}
                      onClick={() => play808({ pitch: p, drive: preset.drive, glide: 0.15, punch: 0.6, sub: 0.85, saturation: preset.saturation, duration: 0.6 })}
                      data-testid={`plugin-808-pad-${p}`}
                      className="border border-zinc-800 hover:border-brand-red hover:bg-brand-red/10 py-2 text-[9px] tracking-[0.2em] uppercase text-ink-mute hover:text-white"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </Module>

              {/* FX RACK */}
              <Module title="EFFECTS RACK" testid="plugin-fx" className="col-span-12 lg:col-span-12">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <RotaryKnob label="REVERB"  value={preset.reverb}       onChange={(v) => u("reverb", v)} testid="plugin-knob-reverb" />
                  <RotaryKnob label="DELAY"   value={preset.delay}        onChange={(v) => u("delay", v)} testid="plugin-knob-delay" />
                  <RotaryKnob label="CHORUS"  value={preset.chorus}       onChange={(v) => u("chorus", v)} testid="plugin-knob-chorus" />
                  <RotaryKnob label="WIDTH"   value={preset.stereo_width} onChange={(v) => u("stereo_width", v)} testid="plugin-knob-width" />
                  <RotaryKnob label="DETUNE"  value={preset.detune_cents} min={0} max={50} step={0.5} unit="¢" onChange={(v) => u("detune_cents", v)} testid="plugin-knob-detune" />
                  <RotaryKnob label="PITCH"   value={preset.pitch_semitones} min={-24} max={24} step={1} unit="st" onChange={(v) => u("pitch_semitones", v)} testid="plugin-knob-pitch" />
                </div>
              </Module>

              {/* KEYBOARD */}
              <Module title="KEYBOARD" tag="2 OCT" testid="plugin-keyboard" className="col-span-12">
                <div className="overflow-x-auto">
                  <Keyboard preset={preset} />
                </div>
              </Module>
            </div>

            {/* Status bar */}
            <div className="mt-5 border-t border-zinc-900 pt-3 flex items-center justify-between text-[9px] tracking-[0.3em] uppercase text-ink-dim flex-wrap gap-2">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse" /> SYNTH ENGINE ONLINE
              </span>
              <span>NOTES PLAYED: <span className="text-white">∞</span></span>
              <span>POLYPHONY 16</span>
              <span>OVERSAMPLE 4x</span>
              <span className="text-brand-red animate-flicker">● REC READY</span>
            </div>
          </div>
        </div>

        {/* CTAs below */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
          <CtaCard Icon={DownloadIcon} title="DOWNLOAD VST" subtitle="Windows + Mac · VST3 / AU" to="/download" cta="GET PLUGIN ▸" testid="cta-download" />
          <CtaCard Icon={Lightning}  title="UNLOCK PRO"   subtitle="Full features · Marketplace · Unlimited AI" to="/pricing"  cta="VIEW PLANS ▸" testid="cta-pricing" />
          <CtaCard Icon={Cpu}        title="OPEN STUDIO"  subtitle="AI presets + saved library" to="/dashboard"  cta="LAUNCH ▸"    testid="cta-studio" />
        </div>
      </div>
    </div>
  );
}

function Module({ title, tag, children, className = "", testid }) {
  return (
    <div className={`border border-zinc-800 bg-bg-surface p-4 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] tracking-[0.3em] uppercase text-brand-red">{title}</span>
        {tag && <span className="text-[9px] tracking-[0.25em] uppercase text-ink-dim">{tag}</span>}
      </div>
      {children}
    </div>
  );
}

function RotaryKnob({ label, value, onChange, min = 0, max = 1, step = 0.01, unit = "", testid, small = false }) {
  const pct = ((value - min) / (max - min));
  const angle = -135 + pct * 270;
  const size = small ? 44 : 52;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[8px] tracking-[0.25em] uppercase text-ink-dim">{label}</span>
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" className="absolute inset-0">
          <circle cx="50" cy="50" r="44" fill="none" stroke="#27272A" strokeWidth="4" />
          <circle
            cx="50" cy="50" r="44" fill="none" stroke="#FF003C" strokeWidth="4"
            strokeDasharray={`${pct * 207} 999`} strokeDashoffset="0"
            transform="rotate(135 50 50)"
            style={{ filter: "drop-shadow(0 0 6px rgba(255,0,60,0.7))" }}
          />
        </svg>
        <div
          className="absolute inset-2 bg-gradient-to-b from-zinc-700 via-zinc-900 to-black border border-zinc-700 rounded-full"
          style={{ transform: `rotate(${angle}deg)` }}
        >
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-0.5 h-3 bg-brand-red" style={{ boxShadow: "0 0 6px #FF003C" }} />
        </div>
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          data-testid={testid}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      <span className="text-[9px] font-mono text-white">
        {typeof value === "number" ? value.toFixed(step < 1 ? 2 : 0) : value}{unit}
      </span>
    </div>
  );
}

function CtaCard({ Icon, title, subtitle, to, cta, testid }) {
  return (
    <Link to={to} className="bg-bg-surface p-7 hover:bg-bg-surface2 transition-colors group" data-testid={testid}>
      <Icon size={24} weight="bold" className="text-brand-red mb-4" />
      <div className="font-display font-black uppercase tracking-tighter text-xl">{title}</div>
      <div className="text-xs text-ink-mute mt-2">{subtitle}</div>
      <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-ink-mute group-hover:text-brand-red transition-colors">{cta}</div>
    </Link>
  );
}
